import React, { useState, useEffect, ChangeEvent, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import MenuItems from "./menu-items";
import ProfileMenu from "./profile-menu";
import { IUser } from "../../types/user";
import { IUserData } from "../../redux/types";
import enpoints from "../../api/endpoints";
import useGetApi from "../../hooks/use-get-api";
import HEADER_MENU_ITEMS, { HeaderMenuItem } from "../../config/header-menu-items";
import { RootState } from "../../redux/store";
import { canAccess } from "../../utils/permissions";
import { LIVE_MARKET_MENUS } from "../../config/live-market";
import { LiveMarketEvent, liveMarketApi } from "../../api/liveMarketApi";
import { updateUserData } from "../../redux/reducers/user";
import { useAppSelector } from "src/redux/redux-hooks";

const LIVE_VIRTUAL_MARKET_GROUP_ID = "810e456b-a344-4ebf-ab12-7313178d2e2d";

const Header: React.FC = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state: RootState) => state.user.userData);
  const { artifacts } = useAppSelector((state) => state.app);
  const userId = userData?.user_id ?? "";

  const [searchText, setSearchText] = useState<string>("");
  const [liveMarketChildren, setLiveMarketChildren] = useState<HeaderMenuItem[] | null>(null);
  const [liveVirtualMarketChildren, setLiveVirtualMarketChildren] = useState<HeaderMenuItem[] | null>(null);
  const lastUserSyncRef = useRef<string>("");

  const { data: adminBalanceData } = useGetApi<IUser>(enpoints.member.getMember(userId));

  useEffect(() => {
    if (adminBalanceData && userData) {
      const shouldKeepLoggedInIdentity = Boolean(userData.is_sub_account);
      const mergedUserData = {
        ...(adminBalanceData as unknown as Partial<IUserData>),
        permissions: userData.permissions,
        is_sub_account: userData.is_sub_account,
        sub_admin_account_id: userData.sub_admin_account_id,
        ...(shouldKeepLoggedInIdentity
          ? {
              user_name: userData.user_name,
              display_name: userData.display_name,
            }
          : {}),
      };
      const nextSignature = JSON.stringify(mergedUserData);
      if (lastUserSyncRef.current === nextSignature) return;

      lastUserSyncRef.current = nextSignature;
      dispatch(updateUserData(mergedUserData));
    }
  }, [adminBalanceData, dispatch, userData]);

  useEffect(() => {
    if (!canAccess(userData, "our_casino")) {
      setLiveMarketChildren(null);
      setLiveVirtualMarketChildren(null);
      return;
    }

    let mounted = true;
    let nextLiveMarketId = 1;
    let nextLiveVirtualMarketId = 1;

    // Keep first 3 submenu entries always static.
    const baseChildren: HeaderMenuItem[] = LIVE_MARKET_MENUS.map((menu) => ({
      id: nextLiveMarketId++,
      title: menu.title,
      link: `/live-market/${menu.key}`,
    }));

    const groupId = process.env.REACT_APP_LIVE_MARKET_GROUP_ID?.trim();

    const loadApiSubmenus = async () => {
      try {
        const [events, virtualEvents] = await Promise.all([
          groupId ? liveMarketApi.fetchBusinessGroupGameEvents(groupId) : Promise.resolve([]),
          liveMarketApi.fetchBusinessGroupGameEvents(LIVE_VIRTUAL_MARKET_GROUP_ID),
        ]);
        const grouped = new Map<string, { gameName: string; events: LiveMarketEvent[] }>();

        events
          .filter((event) => event.is_active && event.is_desktop_visibility)
          .forEach((event) => {
            const key = event.game_id || event.game_name;
            if (!grouped.has(key)) {
              grouped.set(key, { gameName: event.game_name, events: [] });
            }
            grouped.get(key)?.events.push(event);
          });

        const apiChildren: HeaderMenuItem[] = Array.from(grouped.entries())
          .map(([gameKey, value]) => {
            const link =
              value.events.length === 1
                ? `/game/details/${value.events[0].game_id}/${value.events[0].game_event_id}`
                : `/live-market/group/game/${encodeURIComponent(gameKey)}`;

            return {
              id: nextLiveMarketId++,
              title: value.gameName,
              link,
            };
          })
          .sort((a, b) => a.title.localeCompare(b.title));

        const virtualChildren: HeaderMenuItem[] = virtualEvents
          .filter((event) => event.is_active && event.is_desktop_visibility)
          .map((event) => {
            const providerEventId = event.provider_event_id || event.game_event_id;
            const params = new URLSearchParams({
              providerType: event.provider_type || "",
              providerName: event.provider_name || "",
              providerGameId: event.provider_game_id || "",
              eventName: event.event_name || "",
            });
            return {
              id: nextLiveVirtualMarketId++,
              title: event.event_name || event.game_name || "Virtual Event",
              link: `/casinoanalysisuser/${providerEventId}?${params.toString()}`,
            };
          })
          .sort((a, b) => a.title.localeCompare(b.title));

        if (!mounted) return;
        setLiveMarketChildren([...baseChildren, ...apiChildren]);
        setLiveVirtualMarketChildren(virtualChildren);
      } catch {
        if (!mounted) return;
        setLiveMarketChildren(baseChildren);
        setLiveVirtualMarketChildren([]);
      }
    };

    loadApiSubmenus();

    return () => {
      mounted = false;
    };
  }, [userData]);

  const onChangeText = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setSearchText(value);
  };

  const loggedInUserId = userData?.user_id ?? "";
  const canOpenUserList = canAccess(userData, "user_list");

  const filteredMenuItems = useMemo(() => {
    const withDynamicLiveMarket = HEADER_MENU_ITEMS.map((item) => {
      if (item.title === "Live Market" && liveMarketChildren) {
        return {
          ...item,
          children: liveMarketChildren,
        };
      }
      if (item.title === "Live Virtual Market" && liveVirtualMarketChildren) {
        return {
          ...item,
          children: liveVirtualMarketChildren,
        };
      }

      if (item.title === "Live Market" || item.title === "Live Virtual Market") return item;
      return {
        ...item,
        children: item.children,
      };
    });

    const filterMenu = (items: HeaderMenuItem[]): HeaderMenuItem[] =>
      items
        .filter((item) => canAccess(userData, item.permissionKey))
        .map((item) => ({
          ...item,
          children: item.children ? filterMenu(item.children) : undefined,
        }))
        .filter((item) => !item.children || item.children.length > 0);

    return filterMenu(withDynamicLiveMarket);
  }, [userData, liveMarketChildren, liveVirtualMarketChildren]);

  const toggleSidebar = () => {
    document.body.classList.toggle("sidebar-enable");
  };

  return (
    <header id="page-topbar">
      <div className="navbar-header">
        <div className="d-flex">
          <div className="navbar-brand-box">
            {canOpenUserList ? (
              <Link to={`/list-clients/${loggedInUserId}`} className="logo logo-light">
                <span className="logo-lg">
                  <img src={artifacts?.logo} alt="" className="site-logo" />
                </span>
              </Link>
            ) : (
              <span className="logo logo-light" aria-disabled="true">
                <span className="logo-lg">
                  <img src={artifacts?.logo} alt="" className="site-logo" />
                </span>
              </span>
            )}
          </div>

          <button
            id="vertical-menu-btn"
            type="button"
            className="btn btn-sm px-3 font-size-16 header-item"
            onClick={toggleSidebar}
          >
            <i className="fa fa-fw fa-bars" />
          </button>

          <MenuItems data={filteredMenuItems} />
        </div>

        <div className="d-flex user-menu">
          <ProfileMenu />

          <div className="site-searchbox mt-3">
            <div
              className="multiselect"
              tabIndex={-1}
              role="combobox"
              aria-expanded={!!searchText}
              aria-controls="searchuser-list"
            >
              <div className="multiselect__tags">
                <input
                  name="searchuser"
                  type="text"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="All Client"
                  tabIndex={0}
                  className="multiselect__input"
                  value={searchText}
                  onChange={onChangeText}
                  style={{
                    width: searchText ? "auto" : "0px",
                    position: searchText ? "relative" : "absolute",
                    padding: searchText ? undefined : "0px",
                  }}
                />
                {!searchText && <span className="multiselect__placeholder">All Client</span>}
              </div>
            </div>
            <div className="search-icon">
              <i className="fas fa-search-plus" />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
